import React from "react";
import { TrashIcon } from "@heroicons/react/24/outline";

import logger from '../../../../../utils/logger';

const ExpenseRow = ({
  expense,
  index,
  isFixed,
  availableTypes,
  frequencies,
  onUpdate,
  onRemove,
}) => {
  const handleChange = (key, value) => {
    onUpdate(index, key, value);
  };

  // Find the 'Other' frequency option
  const otherFrequency = (frequencies || []).find(
    (f) => f.is_other === true && f.frequency_name === "Other",
  );
  // Only show standard frequencies (is_other === false or 'false') plus 'Other'
  const standardFrequencies = [
    ...(frequencies || []).filter(
      (f) => f.is_other === false || f.is_other === "false",
    ),
    ...(otherFrequency ? [otherFrequency] : []),
  ];
  // Debug: log the filtered standard frequencies and their is_other types
  logger.info(
    "standardFrequencies:",
    standardFrequencies.map((f) => ({
      ...f,
      is_other_type: typeof f.is_other,
    })),
  );
  // Find the selected frequency (type-safe)
  const selectedFrequency = (frequencies || []).find(
    (f) => String(f.frequency_id) === String(expense.frequency_id),
  );
  // If a frequency with is_other === true is selected, show the custom input
  // Otherwise, show the dropdown with only standard frequencies

  return (
    <div
      className={`bg-surface-default dark:bg-surface-default rounded-lg p-4 shadow-sm border transition-all duration-200 mb-4 hover:shadow-md dark:shadow-none ${
        expense.user_id === null
          ? "border-green-200 hover:border-green-300 bg-green-50/50"
          : "border-blue-200 hover:border-blue-300 bg-blue-50/50"
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        {/* Owner Badge */}
        <div className="col-span-1 md:col-span-1 lg:col-span-1 xl:col-span-1">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              expense.user_id === null
                ? "bg-green-100 text-green-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {expense.memberName ||
              (expense.user_id === null ? "Household" : "Unknown")}
          </span>
        </div>

        {/* Type */}
        <div className="relative">
          <select
            value={expense.expense_type_id || ""}
            onChange={(e) => handleChange("expense_type_id", e.target.value)}
            className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 bg-transparent appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {availableTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
            Expense Type
          </label>
        </div>

        {/* Amount */}
        <div className="relative">
          <input
            type="text"
            value={expense.amount === 0 ? "" : expense.amount}
            onChange={(e) => {
              // strip non-numerics except dot
              const raw = e.target.value.replace(/[^0-9.]/g, "");
              handleChange("amount", raw);
            }}
            onBlur={(e) => {
              // format final value or set 0 if invalid
              const floatVal = parseFloat(expense.amount);
              handleChange("amount", isNaN(floatVal) ? 0 : floatVal.toFixed(2));
            }}
            className="peer w-full h-12 border border-gray-300 rounded-lg pl-8 pr-3 pt-3 bg-transparent appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
            placeholder=" "
          />
          <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
            Amount
          </label>
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-500">
            $
          </span>
        </div>

        {/* Frequency */}
        <div className="relative">
          {selectedFrequency &&
          selectedFrequency.is_other &&
          selectedFrequency.frequency_name === "Other" ? (
            <input
              type="number"
              min={1}
              max={365}
              value={expense.custom_frequency_days || ""}
              onChange={(e) =>
                handleChange("custom_frequency_days", e.target.value)
              }
              onBlur={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!val || val < 1 || val > 365) {
                  // Reset to dropdown if invalid or empty
                  handleChange("frequency_id", "");
                  handleChange("custom_frequency_days", "");
                }
              }}
              placeholder="Enter # of days (1-365)"
              className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 bg-transparent appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          ) : (
            <select
              className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 bg-transparent appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={expense.frequency_id || ""}
              onChange={(e) => {
                handleChange("frequency_id", e.target.value);
                // If switching away from custom, clear custom days
                if (!selectedFrequency || !selectedFrequency.is_other) {
                  handleChange("custom_frequency_days", "");
                }
              }}
            >
              <option value="">Select Frequency</option>
              {standardFrequencies.map((f) => (
                <option key={f.frequency_id} value={f.frequency_id}>
                  {f.frequency_name}
                </option>
              ))}
            </select>
          )}
          <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
            Frequency
          </label>
        </div>

        {/* End Date (only for Fixed) */}
        {isFixed ? (
          <div className="relative">
            <input
              type="date"
              value={expense.end_date || ""}
              onChange={(e) => handleChange("end_date", e.target.value)}
              className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 bg-transparent appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
              End Date
            </label>
          </div>
        ) : (
          <div className="pt-3 text-gray-400 text-sm">
            (No end date for discretionary)
          </div>
        )}

        {/* Notes */}
        <div className="relative col-span-1 md:col-span-1 lg:col-span-2 xl:col-span-2">
          <input
            type="text"
            value={expense.notes || ""}
            onChange={(e) => handleChange("notes", e.target.value)}
            className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 bg-transparent appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder=" "
          />
          <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
            Notes
          </label>
        </div>

        {/* Remove Button */}
        <div className="flex items-center justify-end">
          <button
            onClick={() => onRemove(index)}
            className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200 rounded-full hover:bg-red-50"
            title="Remove expense"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseRow;
